import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { Container } from 'reactstrap';

import HelperNavigation from '../../components/common/HelperNavigation';
import PageTitle from '../../components/common/PageTitle';
import CanonicalLink from '../../components/head/CanonicalLink';
import RobotsMeta from '../../components/head/RobotsMeta';
import SEOTitle from '../../components/head/SEOTitle';
import Page from '../../components/layout/Page';
import OrderCancellationContent from '../../components/order-cancellation/OrderCancellationContent';
import messages from '../../components/order-cancellation/messages';
import useOrderCancellationForm from '../../components/order-cancellation/useOrderCancellationForm';
import { buildOrderCancellationPath } from '../../components/order-cancellation/utils';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import ConfigStore from '../../store/ConfigStore';
import LanguageStore from '../../store/LanguageStore';
import OrderStore from '../../store/OrderStore';
import UIStore from '../../store/UIStore';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';

export const OrderCancellationPage = ({
  apiWrapper,
  configStore,
  languageStore,
  orderStore,
  routeService,
  uiStore,
  intl,
}) => {
  const routeMatch = useRouteMatch();
  const orderId = routeMatch?.params?.orderId || null;
  const order = orderId ? orderStore.orders.get(String(orderId)) || null : null;
  const orderCancellationForm = useOrderCancellationForm({
    apiWrapper,
    configStore,
    languageStore,
    order,
    orderId,
    uiStore,
    intl,
  });

  const breadcrumbsPath = routeService.transformBreadCrumbs([
    {
      text: intl.formatMessage(messages.pageTitle),
      url: Paths.OrderCancellation,
    },
  ]);

  return (
    <Page
      name={CommonPage.ORDER_CANCELLATION}
      className="OrderCancellationPage"
    >
      <SEOTitle title={messages.pageTitle} />
      {orderId && <RobotsMeta noindex />}
      <CanonicalLink
        path={routeService.getPath(buildOrderCancellationPath())}
      />
      <Container className="OrderCancellationPage__container">
        <HelperNavigation breadcrumbsPath={breadcrumbsPath} />
        <PageTitle>{intl.formatMessage(messages.pageTitle)}</PageTitle>
        <OrderCancellationContent {...orderCancellationForm} />
      </Container>
    </Page>
  );
};

OrderCancellationPage.propTypes = {
  apiWrapper: PropTypes.shape({
    apiAxios: PropTypes.func.isRequired,
  }).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  uiStore: modelOf(UIStore).isRequired,
};

export default injectIntl(
  inject(
    'apiWrapper',
    'configStore',
    'languageStore',
    'orderStore',
    'routeService',
    'uiStore'
  )(observer(OrderCancellationPage))
);
